import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { useContext, useMemo } from 'react';

import {
  LoadingSpinner,
  SmallButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { JobInvoicing } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import JobsQueryContext, {
  JobsQueryContextProvider,
} from '~pages/Approvals/Files/components/OrdersList/ExpandedBlock/JobsQueryContext';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ApproveChargesPageContext from '../ApproveChargesPageContext';
import ChargesBillingLinesQueryContext, {
  ChargesBillingLinesQueryContextProvider,
} from '../ChargesBillingLinesQueryContext';
import ChargesJobSection from './ChargesJobSection';

interface ChargesOrderSectionProps {
  order: Order;
}

export default function ChargesOrderSection({ order }: ChargesOrderSectionProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { jobsQuery } = useContext(ApproveChargesPageContext);

  return (
    <JobsQueryContextProvider
      companyId={companyId}
      extraJobsQuery={jobsQuery}
      order={order}
      shouldFetchOnMount
    >
      <OrderJobs companyId={companyId} order={order} />
    </JobsQueryContextProvider>
  );
}

interface OrderJobsProps {
  companyId: string;
  order: Order;
}

function OrderJobs({ companyId, order }: OrderJobsProps) {
  const { data: jobs } = useContext(JobsQueryContext);
  const jobIds = useMemo(() => (jobs ?? []).map((job) => job.id), [jobs]);

  if (!jobs) {
    return (
      <Box>
        <LoadingSpinner isVisible sx={{ my: 2 }} />
      </Box>
    );
  }

  return (
    <ChargesBillingLinesQueryContextProvider
      companyId={companyId}
      jobIds={jobIds}
      orderId={order.id}
    >
      <OrderJobRows jobs={jobs} />
    </ChargesBillingLinesQueryContextProvider>
  );
}

interface OrderJobRowsProps {
  jobs: JobInvoicing[];
}

export function OrderJobRows({ jobs }: OrderJobRowsProps) {
  const { isError, isLoading, reload } = useContext(ChargesBillingLinesQueryContext);

  if (isLoading) {
    return (
      <Box>
        <LoadingSpinner isVisible sx={{ my: 2 }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1, px: 2, py: 1.5 }}>
        <Typography color="error" variant="body2">
          {t('approvals.approve_charges.charges_read_failed')}
        </Typography>
        <SmallButton onClick={reload} variant="outlined">
          {t('actions.retry')}
        </SmallButton>
      </Box>
    );
  }

  return jobs.map((job) => <ChargesJobSection key={job.id} job={job} />);
}
